import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { Icon } from '@platform/ui';
import { cx } from '@platform/core/lib/cx';
import { navigationModel, navModes } from '@platform/navigation';
import type { INavModeMenuProps } from './INavModeMenuProps';
import { MenuOverlay } from './MenuOverlay';
import { modeHomePath } from './modeHomePath';
import { useMenuKeys } from './useMenuKeys';
import { useShellNav } from './useShellNav';

/**
 * «Режим навігації» (UIUX top-bar «Navigation modes», прототип v4): кругла кнопка з іконкою шарів; меню 290px — заголовок великими літерами
 * і режими (підпис 600 + опис 12px, ✓ на поточному, підрежими з відступом). Вибір зберігається для користувача й відкриває сторінку
 * режиму: «Адміністратор» — Адміністрування, «Налаштування рішення» — Налаштування, решта — Головну. Кнопка підсвічена (personaBtnBg
 * прототипу), коли режим інший за типовий «Депутат».
 */
export function NavModeMenu({ ws, open, onToggle, onClose }: INavModeMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const { mode, setMode } = useShellNav(ws);
  const button = useRef<HTMLButtonElement>(null);
  const { menu, onKeyDown } = useMenuKeys<HTMLDivElement>(open, onClose, button);
  const title = t('shell.navModeTitle', { mode: label(mode.label) });
  const pick = (key: string) => { setMode(key); onClose(); navigate(modeHomePath(key)); };
  return (
    <div className="tb-anchor tb-mode">
      <button ref={button} type="button" className={cx('po-topbar__icon tb-icon tb-mode__btn', open && 'is-open', mode.key !== navigationModel.defaultMode && 'is-moded')} title={title} aria-label={title} aria-haspopup="menu" aria-expanded={open} onClick={onToggle}>
        <Icon name="layers" size={17} strokeWidth={1.9} />
      </button>
      {open && (
        <>
          <MenuOverlay onClose={onClose} />
          <div ref={menu} role="menu" aria-label={t('shell.navMode')} className="tb-dd tb-dd--right tb-mode__menu" onKeyDown={onKeyDown}>
            <div className="tb-dd__eyebrow">{t('shell.navMode')}</div>
            {navModes().map((m) => (
              <button key={m.key} type="button" role="menuitemradio" aria-checked={m.key === mode.key} className={cx('tb-mode__item', m.key === mode.key && 'is-active', m.parent && 'is-sub')} onClick={() => pick(m.key)}>
                <span className="tb-mode__check" aria-hidden="true">{m.key === mode.key ? '✓' : ''}</span>
                <span className="tb-mode__text">
                  <span className="tb-mode__label">{label(m.label)}</span>
                  <span className="tb-mode__desc">{label(m.description)}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
